<template functional>
    <b-table-column
        :label="props.label"
        :width="props.width"
        :sortable="!props.fields.length && props.sortable"
        :centered="props.centered"
        :header-class="`borderless ${ props.headerClass || '' }`"
        :cell-class="props.cellClass"
        :th-attrs="props.thAttrs"
        :field="props.field"
        :custom-key="props.label || props.tooltip"
        v-bind="Object.assign(data.attrs)">
        <template #header="attrs">
            <slot
                name="header"
                v-bind="attrs">
                <div
                    class="nowrap is-relative"
                    :class="[props.subheadingClass, { 'is-first': !attrs.column.$attrs.index && attrs.column.$attrs.shadow }, { 'pr-5': props.sortable }]"
                    @click="$options.methods.columnSort($event, parent, props)">
                    <slot
                        name="header-content"
                        v-bind="attrs">
                        <LTooltip
                            type="is-dark"
                            :active="!!props.tooltip"
                            :class="{ 'is-clickable': props.sortable }"
                            :label="props.tooltip">
                            <Component
                                v-if="props.sortable && props.fields.length"
                                :is="injections.components.CustomHeaderSort"
                                :fields="$options.computed.customFields(props, parent, attrs.column.$attrs)"
                                icon="sort"
                                icon-size="is-small"
                                icon-class="is-absolute"
                                :column-sort="attrs.column.field"
                                :default-sort="parent.defaultSort"
                                @update:sorting="parent.updateSortings">
                                <div
                                    v-if="props.icon"
                                    :style="`${ $options.computed.size(props) }`"
                                    :class="props.icon"
                                    class="icon-wrapper is-flex is-justify-content-center is-align-items-center">
                                    <Component
                                        :is="injections.components.InlineSvg"
                                        class="is-clickable"
                                        style="vertical-align: middle;"
                                        :src="$options.computed.dynamicIconPath(props, attrs.column.$attrs)"
                                        :alt="props.label"
                                        :aria-label="props.label">
                                    </Component>
                                </div>
                                <template v-else>
                                    {{ attrs.column.label }}
                                </template>
                            </Component>
                            <div
                                v-if="props.icon"
                                :style="`${ $options.computed.size(props) }`"
                                :class="props.icon"
                                class="icon-wrapper is-flex is-justify-content-center is-align-items-center">
                                <Component
                                    :is="injections.components.InlineSvg"
                                    class="is-clickable"
                                    style="vertical-align: middle;"
                                    :src="$options.computed.dynamicIconPath(props, attrs.column.$attrs)"
                                    :alt="props.label"
                                    :aria-label="props.label">
                                </Component>
                            </div>
                            <span v-else-if="!props.fields.length">{{ attrs.column.label }}</span>
                        </LTooltip>
                    </slot>

                    <b-icon
                        v-if="props.sortable && !props.fields.length"
                        :icon="$options.computed.customIcon({ parent, props })"
                        size="is-small"
                        class="sort-icon is-absolute is-inline ml-1 is-clickable is-size-7"
                        :class="{ 'has-text-light': $options.computed.customIcon({ parent, props }) === 'sort' }"
                        both>
                    </b-icon>
                </div>
            </slot>
        </template>

        <template #default="props">
            <slot v-bind="props"></slot>
        </template>
    </b-table-column>
</template>

<script>
  import CustomHeaderSort from "@/components/Common/Table/CustomHeaderSort.vue";
  import InlineSvg from "vue-inline-svg";

  export default {
    name: "GroupColumn",
    inject: {
      components: {
        default: {
          CustomHeaderSort,
          InlineSvg
        }
      }
    },

    props: {
      label: {
        type: String,
        default: null
      },

      width: {
        type: String,
        default: null
      },

      icon: {
        type: String,
        default: null
      },

      iconSize: {
        type: String,
        default: "small"
      },

      sortable: {
        type: Boolean,
        default: false
      },

      field: {
        type: String,
        default: null
      },

      headerClass: {
        type: String,
        default: null
      },

      cellClass: {
        type: String,
        default: null
      },

      centered: {
        type: Boolean,
        default: false
      },

      tooltip: {
        type: String,
        default: null
      },

      fields: {
        type: Array,
        default: () => []
      },

      thAttrs: {
        type: Function,
        default: () => {}
      },

      sortNamespace: {
        type: String,
        default: null
      },

      iconPath: {
        type: String,
        default: null
      },

      subheadingClass: {
        type: String,
        default: null
      }
    },

    computed: {
      customIcon ({ parent, props }) {
        return parent?.$refs.table?.$refs.lTable.currentSortColumn.field === props.field
          ? parent.$refs.table.$refs.lTable.isAsc ? "sort-up" : "sort-down"
          : "sort";
      },

      customFields (props, parent, columnAttrs) {
        if (typeof props.fields[0] === "string") {
          return props.fields.map(field => ({ label: parent.$t(`${ props.sortNamespace || columnAttrs.sortNamespace }.${ field }`), value: field }));
        }
        return props.fields;
      },

      dynamicIconPath (props, columnAttrs) {
        return require(`@/${ props.iconPath || columnAttrs.iconPath }/${ props.icon }.svg`);
      },

      size (props) {
        switch (props.iconSize) {
          case "small":
            return "width: 22px; height: 22px; max-width: 22px; min-width: 22px;";
          case "medium":
            return "width: 38px; height: 38px; max-width: 38px; min-width: 38px;";
          default:
            return "width: 22px; height: 22px; max-width: 22px; min-width: 22px;";
        }
      }
    },

    methods: {
      columnSort ($event, parent, props) {
        const column = parent.$refs.table.$refs.lTable.newColumns.find((c) => c.field === props.field);
        parent.$refs.table.$refs.lTable.sort(column, null, $event);
      }
    }
  };
</script>

<style scoped>

</style>